"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.visitsToLatLngArray = exports.getJitteredAddresses = exports.jitterCoordinatesByZoom = exports.makeRouteData = exports.sortBySchedulerTime = exports.reorderLocationsBasedOnOptimization = exports.fetchRoute = exports.INFO_TYPE = void 0;
/**
 * Route info type for switching between default and optimized routes
 */
var INFO_TYPE;
(function (INFO_TYPE) {
    INFO_TYPE[INFO_TYPE["DEFAULT"] = 0] = "DEFAULT";
    INFO_TYPE[INFO_TYPE["SUGGESTED"] = 1] = "SUGGESTED";
})(INFO_TYPE || (exports.INFO_TYPE = INFO_TYPE = {}));
/**
 * Fetches a route from Google Routes API v2
 */
const fetchRoute = (_a) => __awaiter(void 0, [_a], void 0, function* ({ locations, isOptimize = false, originLocation, destinationLocation, apiKey, }) {
    const origin = originLocation || locations[0];
    const destination = destinationLocation || locations[locations.length - 1];
    const waypoints = locations.slice(originLocation ? 0 : 1, destinationLocation ? locations.length : -1);
    const url = `https://routes.googleapis.com/directions/v2:computeRoutes`;
    const body = {
        origin: {
            location: {
                latLng: {
                    latitude: origin.lat,
                    longitude: origin.lng,
                },
            },
        },
        destination: {
            location: {
                latLng: {
                    latitude: destination.lat,
                    longitude: destination.lng,
                },
            },
        },
        intermediates: waypoints.map(wp => ({
            location: {
                latLng: {
                    latitude: wp.lat,
                    longitude: wp.lng,
                },
            },
        })),
        travelMode: "DRIVE",
        languageCode: "en-US",
        units: "METRIC",
        routingPreference: "TRAFFIC_AWARE",
        optimizeWaypointOrder: isOptimize.toString(),
    };
    let fieldMask = "routes.polyline.encodedPolyline,routes.legs.steps";
    if (isOptimize) {
        fieldMask = fieldMask + ",routes.optimizedIntermediateWaypointIndex";
    }
    const res = yield fetch(url, {
        method: "POST",
        headers: {
            "X-Goog-Api-Key": apiKey,
            "X-Goog-FieldMask": fieldMask,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });
    const data = yield res.json();
    // Return undefined if route is not available (API error or no routes)
    if (data.error || !data.routes || data.routes.length === 0) {
        return undefined;
    }
    return data.routes[0];
});
exports.fetchRoute = fetchRoute;
/**
 * Reorders locations based on optimized waypoint order from Google Routes API
 */
const reorderLocationsBasedOnOptimization = (originalLocations, optimizedIndices) => {
    const reorderedLocations = [];
    if (optimizedIndices.length === 1 && optimizedIndices[0] === -1) {
        return originalLocations;
    }
    for (let i = 0; i < optimizedIndices.length; i++) {
        const originalIndex = optimizedIndices[i];
        reorderedLocations.push(originalLocations[originalIndex]);
    }
    return reorderedLocations;
};
exports.reorderLocationsBasedOnOptimization = reorderLocationsBasedOnOptimization;
/**
 * Sorts scheduled visits by their scheduler time
 */
const sortBySchedulerTime = (data) => {
    if (!Array.isArray(data)) {
        console.error("Input data is not an array");
        return [];
    }
    return [...data].sort((a, b) => {
        if (typeof a.schedulerTime !== "number" ||
            typeof b.schedulerTime !== "number") {
            console.error("Invalid schedulerTime value", { a, b });
            return 0;
        }
        return a.schedulerTime - b.schedulerTime;
    });
};
exports.sortBySchedulerTime = sortBySchedulerTime;
/**
 * Prepares route data based on info type (default or optimized)
 */
const makeRouteData = (routeData, infoType) => {
    return infoType === INFO_TYPE.SUGGESTED
        ? (0, exports.sortBySchedulerTime)(routeData)
        : routeData;
};
exports.makeRouteData = makeRouteData;
/**
 * Applies jitter to coordinates based on zoom level
 * This helps prevent markers from overlapping at the same location
 */
const jitterCoordinatesByZoom = (coordinates, zoom, baseJitter = 0.5) => {
    const [lng, lat] = coordinates;
    const zoomFactor = Math.max(1, 21 - zoom);
    const jitterRadius = baseJitter * zoomFactor;
    const earth = 6378137;
    const dn = (Math.random() - 0.5) * 2 * jitterRadius;
    const de = (Math.random() - 0.5) * 2 * jitterRadius;
    const dLat = dn / earth;
    const dLng = de / (earth * Math.cos((Math.PI * lat) / 180));
    const newLat = lat + dLat * (180 / Math.PI);
    const newLng = lng + dLng * (180 / Math.PI);
    return [newLng, newLat];
};
exports.jitterCoordinatesByZoom = jitterCoordinatesByZoom;
/**
 * Returns jittered addresses for markers at the same location
 */
const getJitteredAddresses = (addresses, zoom) => {
    if (!(addresses === null || addresses === void 0 ? void 0 : addresses.length))
        return addresses;
    const locationMap = {};
    addresses.forEach(address => {
        if (!address.location || !Array.isArray(address.location.coordinates)) {
            return;
        }
        const [lat, lng] = address.location.coordinates;
        const key = `${lat.toFixed(5)}_${lng.toFixed(5)}`;
        if (!locationMap[key]) {
            locationMap[key] = [];
        }
        locationMap[key].push(address);
    });
    const jitteredList = [];
    Object.values(locationMap).forEach(group => {
        if (group.length === 1) {
            jitteredList.push(group[0]);
        }
        else {
            group.forEach(marker => {
                if (!marker.location || !Array.isArray(marker.location.coordinates)) {
                    jitteredList.push(marker);
                    return;
                }
                const [newLat, newLng] = (0, exports.jitterCoordinatesByZoom)(marker.location.coordinates, zoom);
                jitteredList.push(Object.assign(Object.assign({}, marker), { location: Object.assign(Object.assign({}, marker.location), { coordinates: [newLat, newLng], type: "Point" }) }));
            });
        }
    });
    return jitteredList;
};
exports.getJitteredAddresses = getJitteredAddresses;
/**
 * Converts scheduled visits to lat/lng array for route calculation
 */
const visitsToLatLngArray = (visits) => {
    return visits
        .filter(v => v.location && Array.isArray(v.location.coordinates))
        .map(v => ({
        lat: v.location.coordinates[1],
        lng: v.location.coordinates[0],
    }));
};
exports.visitsToLatLngArray = visitsToLatLngArray;
